/**
 * นำเข้ารายการบวก/หักจากไฟล์ชีต — เมนู เงินเดือน > นำเข้ารายการบวก/หักจากไฟล์ชีต (TN Riches 24 ก.ย. 2569 · เปลี่ยนชื่อจาก "นำเข้าค่าคอม" fb#112)
 *
 * ดาวน์โหลดไฟล์ชีตเงินเดือนจากหน้าซ้อม → ฝ่ายค่าคอมกรอก Commission/เงินพิเศษ/ความผิดพลาด ฯลฯ →
 * อัปโหลดที่นี่ (CSV หรือ Excel) → ตรวจยอด → บันทึกเป็นรายการปรับของงวด → กดรันร่างใหม่
 * กติกาเงิน/กันซ้ำอยู่ที่ worker lib/sheet-adjustments.ts
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, CircleSlash, Save } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { apiFetch } from '../../lib/api-client';
import { useRehearsalOptions } from '../../hooks';

interface Total { key: string; column: string; label: string; direction: 'add' | 'deduct'; file: number; import: number; conflict: number; rejected: number }
interface Preview {
  period: string;
  plan_hash: string;
  blocking: string[];
  saved: boolean;
  inserted?: number;
  stale_runs?: number;
  data_rows: number;
  matched_rows: number;
  empty_rows: number;
  rejected: Array<{ row: number; name: string; reason: string }>;
  total_rows: Array<{ row: number; name: string }>;
  broken_totals?: Array<{ row: number; name: string; column: string; parts_sum: number; formula: string }>;
  conflicts: Array<{ row: number; emp_code: string; name: string; column: string; amount: number; existing: string }>;
  missing_columns: string[];
  rows_without_month: number;
  totals: Total[];
  insert_count: number;
  inserts: Array<{ row: number; emp_code: string; name: string; label: string; direction: 'add' | 'deduct'; amount: number }>;
  inserts_shown: number;
  replaces_previous: { rows: number; total: number };
}

const baht = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Google Sheets = UTF-8 · Excel "CSV" บน Windows ไทย = windows-874 */
async function readCsvText(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-874').decode(buf);
  }
}

/** Excel หลายแท็บ → เลือกแท็บที่มีหัว "ชื่อนามสกุล" + "Commission" แล้วแปลงเป็น CSV (ค่าที่เห็นในเซลล์ ไม่ใช่สูตร) */
async function readXlsx(file: File): Promise<{ sheets: Array<{ name: string; csv: string }> }> {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  // เก็บแถวว่าง + เริ่มที่ A1 → เลขแถว/คอลัมน์ในข้อความตรงกับชีตจริง (สูตรที่บอกให้ใส่กลับต้องชี้แถวถูก · astra fb#519)
  const sheets = wb.SheetNames.map((name) => {
    const ws = wb.Sheets[name]!;
    if (ws['!ref']) { const r = XLSX.utils.decode_range(ws['!ref']); r.s.r = 0; r.s.c = 0; ws['!ref'] = XLSX.utils.encode_range(r); }
    return { name, csv: XLSX.utils.sheet_to_csv(ws, { blankrows: true }) };
  })
    .filter((s) => /ชื่อนามสกุล/.test(s.csv) && /Commission/i.test(s.csv));
  return { sheets };
}

export function SheetAdjustmentsImport() {
  const toast = useToast();
  const { data: opt } = useRehearsalOptions();
  const [period, setPeriod] = useState('');
  const chosen = period || opt?.current_period || '';
  const [fileName, setFileName] = useState('');
  const [sheets, setSheets] = useState<Array<{ name: string; csv: string }>>([]);
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState<'' | 'read' | 'preview' | 'save'>('');

  async function runPreview(text: string, p: string) {
    setBusy('preview');
    setPreview(null);
    setChecked(false);
    try {
      const res = await apiFetch<Preview>('/owner/payroll/sheet-adjustments', { method: 'POST', body: { period: p, csv_text: text } });
      setPreview(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตรวจไฟล์ไม่สำเร็จ');
    } finally {
      setBusy('');
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !chosen) return;
    setBusy('read');
    setFileName(file.name);
    setPreview(null);
    try {
      if (/\.xlsx?$/i.test(file.name)) {
        const { sheets: found } = await readXlsx(file);
        if (found.length === 0) {
          toast.error('ไม่พบแท็บที่มีหัว "ชื่อนามสกุล" และ "Commission" ในไฟล์ Excel นี้');
          setBusy('');
          return;
        }
        setSheets(found);
        setCsvText(found[0]!.csv);
        await runPreview(found[0]!.csv, chosen);
      } else {
        const text = await readCsvText(file);
        setSheets([]);
        setCsvText(text);
        await runPreview(text, chosen);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'อ่านไฟล์ไม่สำเร็จ');
      setBusy('');
    }
  }

  async function save() {
    if (!preview) return;
    setBusy('save');
    try {
      const res = await apiFetch<Preview>('/owner/payroll/sheet-adjustments', {
        method: 'POST',
        body: { period: preview.period, csv_text: csvText, plan_hash: preview.plan_hash }, // apiFetch แปลง JSON ให้เอง
      });
      setPreview(res);
      toast.success(`บันทึก ${res.inserted ?? 0} รายการเข้างวด ${res.period} แล้ว — กดรันร่างใหม่ที่ รอบเงินเดือน`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setBusy('');
    }
  }

  const periods = opt?.periods ?? [];
  const canSave = !!preview && !preview.saved && preview.blocking.length === 0 && (preview.insert_count > 0 || preview.replaces_previous.rows > 0) && checked && !busy;

  return (
    <AdminLayout title="นำเข้ารายการบวก/หักจากไฟล์ชีต" subtitle="กรอกเงินเพิ่ม/รายการหักของทุกคนในไฟล์ชีตเงินเดือนครั้งเดียว แล้วอัปโหลดเข้าร่างเงินเดือนของงวด ไม่ต้องบวก-หักทีละคน">
      <div className="card p-4 mb-4 space-y-3">
        <ol className="text-sm text-gray-600 list-decimal ml-5 space-y-1">
          <li>ดาวน์โหลดไฟล์ชีตเงินเดือนจากหน้า <Link to="/owner/rehearsal" className="text-brand-700 underline">ซ้อมทำเงินเดือน</Link> แล้วกรอกยอดของแต่ละคน (Excel หรือ Google Sheets ก็ได้)</li>
          <li>
            ช่องที่ระบบอ่าน — <b>เงินเพิ่ม:</b> ค่านายหน้า (I) · Commission (T) · เงินพิเศษ (U) · <b>รายการหัก:</b> ความผิดพลาด (AK) · ค่าเสื้อบริษัท (AM) · ประกันร้าน (AN)
            <div className="text-xs text-gray-500 mt-0.5">
              กรอกที่ช่องย่อยเหล่านี้ อย่าพิมพ์ทับช่องรวม (เงินได้อื่นๆ · เงินประกัน · อื่นๆ) — ถ้ายอดช่องรวมไม่ตรงช่องย่อย ระบบจะไม่นำเข้าแถวนั้นและบอกเหตุผล ·
              ค่าล่วงเวลา · ขาด/ลา/มาสาย · สปส. · ภงด 1 ระบบคิดเองจากเวลาและกติกาในระบบ ไม่อ่านจากไฟล์
            </div>
          </li>
          <li>อัปโหลดไฟล์ที่กรอกแล้ว (CSV หรือ Excel) ตรวจยอด แล้วกดบันทึก</li>
          <li>ไปที่ <Link to="/owner/cycle" className="text-brand-700 underline">รอบเงินเดือน</Link> กด "รันร่าง" ใหม่ ยอดจึงจะเข้าร่าง</li>
        </ol>
        <p className="text-xs text-gray-500">
          นำเข้าซ้ำได้ — ระบบแทนที่รายการจากไฟล์รอบก่อนของงวดนั้นทั้งหมด ยอดไม่เบิ้ล · รายการที่กรอกเองในระบบไม่ถูกแตะ
          และถ้าคนนั้นมีรายการกรอกเองหมวดเดียวกันอยู่แล้ว (เช่น "ค่าเสื้อ") ระบบข้ามช่องนั้นให้ ไม่หักซ้ำ
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
          <label className="block min-w-0">
            <span className="label">งวดที่จะนำเข้า</span>
            <select className="input w-full" value={chosen} onChange={(e) => { setPeriod(e.target.value); if (csvText) void runPreview(csvText, e.target.value); }} disabled={!!busy || periods.length === 0}>
              {periods.map((p) => <option key={p.period} value={p.period}>{p.period} · {p.label}</option>)}
            </select>
          </label>
          <label className={`btn-primary cursor-pointer whitespace-nowrap ${busy || !chosen ? 'opacity-60 pointer-events-none' : ''}`}>
            <Upload className="w-4 h-4" /> {busy === 'read' || busy === 'preview' ? 'กำลังตรวจไฟล์…' : 'อัปโหลดไฟล์ (CSV / Excel)'}
            <input type="file" accept=".csv,text/csv,.xlsx,.xls" className="hidden" onChange={(e) => void handleFile(e)} disabled={!!busy || !chosen} />
          </label>
        </div>
        {fileName && <p className="text-sm text-gray-600 flex items-center gap-1 min-w-0"><FileSpreadsheet className="w-4 h-4 shrink-0" /><span className="truncate">{fileName}</span></p>}
        {sheets.length > 1 && (
          <label className="block">
            <span className="label">แท็บในไฟล์ Excel</span>
            <select className="input w-full" onChange={(e) => { const s = sheets[Number(e.target.value)]!; setCsvText(s.csv); void runPreview(s.csv, chosen); }} disabled={!!busy}>
              {sheets.map((s, i) => <option key={s.name} value={i}>{s.name}</option>)}
            </select>
          </label>
        )}
      </div>

      {busy === 'preview' && (
        <div className="card p-4 mb-4 space-y-2">
          <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse" />
          <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
        </div>
      )}

      {preview && (
        <>
          {preview.saved && (
            <div className="card p-3 mb-4 border-emerald-200 bg-emerald-50 text-sm text-emerald-900 flex items-start gap-1.5">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>บันทึกแล้ว {preview.inserted} รายการ เข้างวด {preview.period} — ไปที่ <Link to="/owner/cycle" className="underline">รอบเงินเดือน</Link> แล้วกด "รันร่าง" ใหม่ ยอดจึงจะเข้าร่าง</span>
            </div>
          )}
          {preview.saved && (preview.stale_runs ?? 0) > 0 && (
            <div className="card p-3 mb-4 border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                ร่างเงินเดือนงวดนี้ที่รันไว้แล้ว {preview.stale_runs} คน ยังเป็นตัวเลขเก่า ไม่รวมยอดที่เพิ่งนำเข้า —
                ต้องกด "รันร่าง" ใหม่ก่อนส่งผู้บริหารอนุมัติ ไม่งั้นจะจ่ายขาด
              </span>
            </div>
          )}

          <div className="card p-4 mb-4">
            <div className="font-semibold mb-2">ยอดต่อช่อง (งวด {preview.period})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">ช่องในชีต</th>
                    <th className="px-2 py-1.5 text-right whitespace-nowrap">ในไฟล์</th>
                    <th className="px-2 py-1.5 text-right whitespace-nowrap">นำเข้า</th>
                    <th className="px-2 py-1.5 text-right whitespace-nowrap">ข้าม (กรอกเองแล้ว)</th>
                    <th className="px-2 py-1.5 text-right whitespace-nowrap">แถวที่ไม่เข้า</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.totals.map((t) => (
                    <tr key={t.key} className="border-t border-gray-100">
                      <td className="px-2 py-1.5 whitespace-nowrap">{t.column} <span className="text-xs text-gray-400">{t.direction === 'add' ? 'เงินเพิ่ม' : 'รายการหัก'}</span></td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{baht(t.file)}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap font-medium">{baht(t.import)}</td>
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap ${t.conflict > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{baht(t.conflict)}</td>
                      <td className={`px-2 py-1.5 text-right whitespace-nowrap ${t.rejected > 0 ? 'text-red-700' : 'text-gray-400'}`}>{baht(t.rejected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              แถวในไฟล์ {preview.data_rows} แถว = จับคู่พนักงานได้ {preview.matched_rows} (ไม่มีรายการ {preview.empty_rows}) · ไม่เข้า {preview.rejected.length} · แถวยอดรวม {preview.total_rows.length}
              {preview.rows_without_month > 0 && ` · ${preview.rows_without_month} แถวไม่มีช่องเดือน (ตรวจงวดไม่ได้)`}
            </p>
            {preview.missing_columns.length > 0 && (
              <p className="text-xs text-amber-700 mt-1">ไม่พบช่อง: {preview.missing_columns.join(', ')} — ช่องเหล่านี้จะไม่ถูกนำเข้า</p>
            )}
            {preview.replaces_previous.rows > 0 && (
              <p className="text-xs text-amber-700 mt-1">
                มีรายการจากไฟล์รอบก่อน {preview.replaces_previous.rows} รายการ ({baht(preview.replaces_previous.total)} ฿) — บันทึกครั้งนี้จะแทนที่ทั้งหมด
              </p>
            )}
          </div>

          {!preview.saved && preview.blocking.length > 0 && (
            <div className="card p-3 mb-4 border-red-300 bg-red-50 text-sm text-red-800">
              <div className="font-medium flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> ยังบันทึกไม่ได้</div>
              <ul className="list-disc ml-5">{preview.blocking.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          )}

          {preview.rejected.length > 0 && (
            <div className="card p-4 mb-4 border-red-200">
              <div className="font-semibold mb-1 flex items-center gap-1.5 text-red-700"><CircleSlash className="w-4 h-4" /> แถวที่ไม่นำเข้า ({preview.rejected.length}) — แก้ในไฟล์แล้วอัปโหลดใหม่</div>
              <ul className="text-sm space-y-1">
                {preview.rejected.map((r) => <li key={r.row}><span className="text-gray-500">แถว {r.row}</span> {r.name} — <span className="text-red-700">{r.reason}</span></li>)}
              </ul>
            </div>
          )}

          {(preview.broken_totals?.length ?? 0) > 0 && (
            <div className="card p-4 mb-4 border-amber-200">
              <div className="font-semibold mb-1 flex items-center gap-1.5 text-amber-800"><AlertTriangle className="w-4 h-4" /> ช่องรวมในไฟล์ไม่เป็นสูตรแล้ว ({preview.broken_totals!.length}) — {preview.saved ? 'นำเข้าตามช่องย่อยแล้ว' : 'จะนำเข้าตามช่องย่อยเมื่อกดบันทึก'}</div>
              <p className="text-xs text-gray-600 mb-2">ระบบใช้ยอดจากช่องย่อยคิดเงินเดือน ไม่ต้องแก้ไฟล์เพื่อนำเข้า · แต่ยอดรายรับ/รวมรับสุทธิในไฟล์จะไม่ขึ้น จนกว่าจะใส่สูตรช่องรวมกลับ (คัดลอกสูตรจากแถวอื่นในคอลัมน์เดียวกันก็ได้)</p>
              <ul className="text-sm space-y-1">
                {preview.broken_totals!.map((b) => <li key={`${b.row}-${b.column}`}><span className="text-gray-500">แถว {b.row}</span> {b.name} · ช่อง "{b.column}" เป็น 0 แต่ช่องย่อยรวม {baht(b.parts_sum)} — ใส่สูตร <span className="font-mono">{b.formula}</span> กลับในช่องนั้น</li>)}
              </ul>
            </div>
          )}

          {preview.conflicts.length > 0 && (
            <div className="card p-4 mb-4 border-amber-200">
              <div className="font-semibold mb-1 flex items-center gap-1.5 text-amber-800"><AlertTriangle className="w-4 h-4" /> ข้ามเพราะมีรายการกรอกเองแล้ว ({preview.conflicts.length})</div>
              <p className="text-xs text-gray-600 mb-2">ถ้าต้องการใช้ยอดจากไฟล์แทน ให้ลบรายการที่กรอกเองในระบบก่อน แล้วอัปโหลดใหม่</p>
              <ul className="text-sm space-y-1">
                {preview.conflicts.map((c) => <li key={`${c.row}-${c.column}`}><span className="text-gray-500">แถว {c.row}</span> {c.name} · {c.column} {baht(c.amount)} — มีอยู่แล้ว: {c.existing}</li>)}
              </ul>
            </div>
          )}

          {!preview.saved && (
            <div className="card p-4 mb-4 space-y-3">
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1 w-4 h-4" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                <span>ตรวจยอดต่อช่องแล้ว ตรงกับไฟล์ที่กรอกมา</span>
              </label>
              <div className="flex justify-end">
                <button type="button" onClick={() => void save()} disabled={!canSave} className="btn-primary">
                  <Save className="w-4 h-4" /> {busy === 'save' ? 'กำลังบันทึก…' : `บันทึก ${preview.insert_count} รายการเข้างวด ${preview.period}`}
                </button>
              </div>
            </div>
          )}

          {preview.inserts.length > 0 && (
            <div className="card p-4">
              <div className="font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-brand-600" /> รายการที่จะเข้า
                {preview.inserts_shown < preview.insert_count && <span className="text-xs text-gray-500 font-normal">(แสดง {preview.inserts_shown} จาก {preview.insert_count} — บันทึกครบทุกรายการ)</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr><th className="px-2 py-1.5 text-left">แถว</th><th className="px-2 py-1.5 text-left">พนักงาน</th><th className="px-2 py-1.5 text-left">รายการ</th><th className="px-2 py-1.5 text-right">จำนวน</th></tr>
                  </thead>
                  <tbody>
                    {preview.inserts.map((i) => (
                      <tr key={`${i.emp_code}-${i.label}`} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">{i.row}</td>
                        <td className="px-2 py-1.5">{i.name}<span className="text-gray-400"> · {i.emp_code}</span></td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{i.label}</td>
                        <td className={`px-2 py-1.5 text-right whitespace-nowrap ${i.direction === 'deduct' ? 'text-red-700' : ''}`}>{i.direction === 'deduct' ? '−' : ''}{baht(i.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
